import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import { axiosPrivate } from 'app/utils';

import type { PlusWebsitesResponse } from '../../../hooks/usePlusWebsites';

interface UpdateWebsiteExternalHomepageUrlParams {
  websiteId: string;
  /** Pass null to remove the external homepage URL. */
  externalHomepageUrl: string | null;
}

function updateWebsiteExternalHomepageUrl({
  websiteId,
  externalHomepageUrl,
}: UpdateWebsiteExternalHomepageUrlParams) {
  // The endpoint removes the external homepage URL when the field is omitted.
  const body = externalHomepageUrl === null ? {} : { externalHomepageUrl };
  return axiosPrivate
    .put(`/v3/support/plus-websites/${websiteId}/external-homepage-urls`, body)
    .then((r) => r.data);
}

export function useUpdateWebsiteExternalHomepageUrl() {
  const queryClient = useQueryClient();
  const queryKey = ['plus-websites'];

  return useMutation({
    mutationFn: updateWebsiteExternalHomepageUrl,
    // Deliberately no invalidation after the write: the list endpoint can lag
    // behind this PUT, so an immediate refetch may return the old value and
    // overwrite this cache update (and refetchOnWindowFocus is disabled, so it
    // would stick until a remount). The accepted PUT is the source of truth.
    onSuccess: (
      _data,
      { websiteId, externalHomepageUrl }: UpdateWebsiteExternalHomepageUrlParams
    ) => {
      queryClient.setQueriesData<AxiosResponse<PlusWebsitesResponse>>({ queryKey }, (old) =>
        old
          ? {
              ...old,
              data: {
                ...old.data,
                plusWebsites: old.data.plusWebsites.map((website) =>
                  website.id === websiteId ? { ...website, externalHomepageUrl } : website
                ),
              },
            }
          : old
      );
    },
    meta: {
      successMessage: 'External homepage URL updated successfully',
      errorMessage: 'Failed to update external homepage URL',
    },
  });
}
